import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ISPAConfig } from "config/api";
import { ToastContext } from "context/ToastContext";
import { PaymentSection } from "pages/OrderOverview/components/PaymentSection";
import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useCreateOrderPaymentIntentMutation } from "store/queries/order";
import { IPaymentPlan } from "types/Payment";

import ErrorMessage from "../ErrorMessage";

const getPublishableKey = () => {
  const config = window?.config as ISPAConfig;

  if (!config) {
    throw new Error("Config not found");
  }

  return config.stripe.publishableKey;
};

export default function PaymentPlan({
  expirationDays,
  orderInfo,
  showPaymentSection,
  setShowPaymentSection,
  clearPrice,
  totalSaving,
  fullPrice,
}: any) {
  const { id: externalId } = useParams();
  const { current: toastElement } = useContext(ToastContext);

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    setStripePromise(loadStripe(getPublishableKey()));
  }, []);

  const [
    createOrderPaymentIntent,
    { isError: isStripeError, isLoading: isStripeLoading },
  ] = useCreateOrderPaymentIntentMutation();

  const [clientSecret, setClientSecret] = useState("");
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    createOrderPaymentIntent({
      orderId: externalId || "",
      paymentPlan: true,
      payRemainingPrice: false,
    })
      .unwrap()
      .then(
        ({
          data,
          stripeCustomerId: responseStripeCustomerId,
        }: {
          data: any;
          stripeCustomerId: string;
        }) => {
          setClientSecret(data.client_secret);
          setStripeCustomerId(responseStripeCustomerId);
          setPaymentInitError(null);
        }
      )
      .catch((error) => {
        console.error("Error creating payment intent:", error);
        const errorMessage =
          error?.data?.messages?.[0] ||
          "There was an issue setting up your payment plan. Please try again.";
        setPaymentInitError(errorMessage);

        toastElement?.show({
          severity: "error",
          summary: "Payment Plan Setup Error",
          detail: errorMessage,
        });
      });
  }, [externalId, isRetrying]);

  const handleRetry = () => {
    setIsRetrying(!isRetrying); // Toggle to trigger useEffect
  };

  // Show error UI if payment intent creation failed
  if (paymentInitError) {
    return (
      <ErrorMessage
        paymentInitError={paymentInitError}
        buttonDisabled={isStripeLoading}
        onTryAgain={handleRetry}
      />
    );
  }

  if (!clientSecret?.length) {
    return <LoadingSpinner />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        // HACK TO HIDE THE TERMS TEXT IN STRIPE EMBEDDED ELEMENT
        appearance: {
          rules: {
            ".TermsText": {
              color: "transparent",
              fontSize: "0px",
            },
          },
        },
      }}
    >
      <PaymentSection
        expirationDays={expirationDays}
        order={orderInfo}
        prices={{ clearPrice, totalSaving, fullPrice }}
        onReturnClick={() => {
          setShowPaymentSection({
            paymentPlan: false,
            paymentSection: false,
            paymentSuccess: false,
          });
        }}
        showPaymentSection={showPaymentSection}
        setShowPaymentSection={setShowPaymentSection}
        stripeCustomerId={stripeCustomerId}
        isDownPaymentOnly={
          orderInfo?.paymentPlan?.find((plan: IPaymentPlan) => plan?.active)
            ?.isDownPaymentOnly
        }
      />
    </Elements>
  );
}
